import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, ArrowLeft } from 'lucide-react';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="text-white" size={28} strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-light tracking-wide text-black mb-2">
              Admin Login
            </h1>
            <p className="text-gray-500 text-sm font-light">Sign in to access the dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-light text-gray-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@bcstore.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-gray-200 focus:border-black focus:ring-0 font-light"
                required
                data-testid="admin-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-light text-gray-700">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-gray-200 focus:border-black focus:ring-0 font-light"
                required
                data-testid="admin-password-input"
              />
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-black text-white text-sm font-light tracking-widest hover:bg-gray-900 transition-colors"
              disabled={loading}
              data-testid="admin-login-button"
            >
              {loading ? 'SIGNING IN...' : 'SIGN IN'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center text-sm text-gray-500 hover:text-black transition-colors font-light"
            >
              <ArrowLeft size={16} className="mr-2" strokeWidth={1.5} />
              Back to Home
            </button>
          </div>
        </div>

        <div className="mt-4 text-center text-xs text-gray-400 font-light">
          <p>Demo: admin@bcstore.com / admin123</p>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;